/**
 * Credit Card Validation Utilities
 * Contains functions for validating credit card information
 */

export interface CardBrand {
  name: string;
  pattern: RegExp;
  length: number[];
  cvcLength: number[];
}

// Card brand patterns and configurations
export const CARD_BRANDS: Record<string, CardBrand> = {
  VISA: {
    name: 'VISA',
    pattern: /^4/,
    length: [13, 16, 19],
    cvcLength: [3],
  },
  MASTERCARD: {
    name: 'MASTERCARD',
    pattern: /^(5[1-5]|222[1-9]|22[3-9]|2[3-6]|27[01]|2720)/,
    length: [16],
    cvcLength: [3],
  },
  ELO: {
    name: 'ELO',
    pattern: /^(4011|4312|4389|4514|4576|5041|5066|5067|6277|6362|6363|6500|6504|6505|6506|6507|6509|6516|6550)/,
    length: [16],
    cvcLength: [3],
  },
  AMEX: {
    name: 'AMEX',
    pattern: /^3[47]/,
    length: [15],
    cvcLength: [4],
  },
  DISCOVER: {
    name: 'DISCOVER',
    pattern: /^(6011|622|64|65)/,
    length: [16],
    cvcLength: [3],
  },
  DINERS: {
    name: 'DINERS',
    pattern: /^(36|38|30[0-5])/,
    length: [14],
    cvcLength: [3],
  },
  JCB: {
    name: 'JCB',
    pattern: /^35/,
    length: [16],
    cvcLength: [3],
  },
};

/**
 * Detects the credit card brand based on the card number
 * @param cardNumber - The credit card number (with or without spaces/dashes)
 * @returns The detected card brand or null if not recognized
 */
export const detectCardBrand = (cardNumber: string): string | null => {
  const cleanedNumber = cardNumber.replace(/\s|-/g, '');
  
  for (const [brand, config] of Object.entries(CARD_BRANDS)) {
    if (config.pattern.test(cleanedNumber)) {
      return brand;
    }
  }
  
  return null;
};

/**
 * Validates a credit card number using the Luhn algorithm
 * @param cardNumber - The credit card number to validate
 * @returns True if the card number is valid, false otherwise
 */
export const validateLuhn = (cardNumber: string): boolean => {
  const cleanedNumber = cardNumber.replace(/\s|-/g, '');
  
  if (!/^\d+$/.test(cleanedNumber)) {
    return false;
  }
  
  let sum = 0;
  let isEven = false;
  
  // Loop through values starting from the rightmost digit
  for (let i = cleanedNumber.length - 1; i >= 0; i--) {
    let digit = parseInt(cleanedNumber[i], 10);
    
    if (isEven) {
      digit *= 2;
      if (digit > 9) {
        digit -= 9;
      }
    }
    
    sum += digit;
    isEven = !isEven;
  }
  
  return sum % 10 === 0;
};

/**
 * Validates the card number length for a specific brand
 * @param cardNumber - The credit card number
 * @param brand - The card brand
 * @returns True if the length is valid for the brand
 */
export const validateCardLength = (cardNumber: string, brand?: string): boolean => {
  const cleanedNumber = cardNumber.replace(/\s|-/g, '');
  const detectedBrand = brand || detectCardBrand(cleanedNumber);
  
  if (!detectedBrand || !CARD_BRANDS[detectedBrand]) {
    // If brand is not detected, check if length is within common ranges
    return cleanedNumber.length >= 13 && cleanedNumber.length <= 19;
  }
  
  return CARD_BRANDS[detectedBrand].length.includes(cleanedNumber.length);
};

/**
 * Validates the CVV/CVC code length for a specific brand
 * @param cvc - The CVV/CVC code
 * @param brand - The card brand
 * @returns True if the CVC length is valid for the brand
 */
export const validateCVCLength = (cvc: string, brand?: string): boolean => {
  if (!brand || !CARD_BRANDS[brand]) {
    // Default validation: 3 or 4 digits
    return /^\d{3,4}$/.test(cvc);
  }
  
  return CARD_BRANDS[brand].cvcLength.includes(cvc.length);
};

/**
 * Validates if the card expiry date is in the future
 * @param month - The expiry month (01-12)
 * @param year - The expiry year (2 or 4 digits)
 * @returns True if the card is not expired
 */
export const validateExpiryDate = (month: string, year: string): boolean => {
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth() + 1; // getMonth() returns 0-11
  
  const expMonth = parseInt(month, 10);
  let expYear = parseInt(year, 10);
  
  // Convert 2-digit year to 4-digit year
  if (expYear < 100) {
    expYear += Math.floor(currentYear / 100) * 100;
  }
  
  // Check if month is valid
  if (expMonth < 1 || expMonth > 12) {
    return false;
  }
  
  // Check if card is expired
  if (expYear < currentYear) {
    return false;
  }
  
  if (expYear === currentYear && expMonth < currentMonth) {
    return false;
  }
  
  // Check if expiry is too far in the future (more than 20 years)
  if (expYear > currentYear + 20) {
    return false;
  }
  
  return true;
};

/**
 * Comprehensive credit card validation
 * @param cardNumber - The credit card number
 * @param brand - The card brand (optional, will be auto-detected if not provided)
 * @returns Object with validation result and error message
 */
export const validateCreditCard = (
  cardNumber: string,
  brand?: string
): { isValid: boolean; error?: string; detectedBrand?: string } => {
  const cleanedNumber = cardNumber.replace(/\s|-/g, '');
  
  // Check if it contains only digits
  if (!/^\d+$/.test(cleanedNumber)) {
    return { isValid: false, error: 'O número do cartão deve conter apenas dígitos' };
  }
  
  // Detect brand
  const detectedBrand = brand || detectCardBrand(cleanedNumber);
  
  if (!detectedBrand) {
    return { isValid: false, error: 'Bandeira do cartão não reconhecida' };
  }
  
  // Validate length
  if (!validateCardLength(cleanedNumber, detectedBrand)) {
    return { 
      isValid: false, 
      error: `Comprimento inválido do cartão para ${detectedBrand}`,
      detectedBrand 
    };
  }
  
  // Validate using Luhn algorithm
  if (!validateLuhn(cleanedNumber)) {
    return { 
      isValid: false, 
      error: 'Número do cartão inválido',
      detectedBrand 
    };
  }
  
  return { isValid: true, detectedBrand };
};

/**
 * Formats a card number with spaces for display
 * @param cardNumber - The credit card number
 * @returns Formatted card number with spaces
 */
export const formatCardNumber = (cardNumber: string): string => {
  const cleanedNumber = cardNumber.replace(/\s|-/g, '');
  const detectedBrand = detectCardBrand(cleanedNumber);
  
  // American Express format: XXXX XXXXXX XXXXX
  if (detectedBrand === 'AMEX') {
    return cleanedNumber.replace(/(\d{4})(\d{6})(\d{5})/, '$1 $2 $3').trim();
  }
  
  // Default format: XXXX XXXX XXXX XXXX
  return cleanedNumber.replace(/(\d{4})/g, '$1 ').trim();
};

/**
 * Masks a card number for display (shows only last 4 digits)
 * @param cardNumber - The credit card number
 * @returns Masked card number (e.g., "**** **** **** 1234")
 */
export const maskCardNumber = (cardNumber: string): string => {
  const cleanedNumber = cardNumber.replace(/\s|-/g, '');
  
  if (cleanedNumber.length < 4) {
    return '*'.repeat(cleanedNumber.length);
  }
  
  const lastFour = cleanedNumber.slice(-4);
  const maskedLength = cleanedNumber.length - 4;
  const masked = '*'.repeat(maskedLength);
  
  // Format with spaces
  const maskedNumber = masked + lastFour;
  return formatCardNumber(maskedNumber);
};

/**
 * Validates CPF (Brazilian tax ID)
 * @param cpf - The CPF number
 * @returns True if valid
 */
export const validateCPF = (cpf: string): boolean => {
  const cleanedCPF = cpf.replace(/\D/g, '');
  
  if (cleanedCPF.length !== 11) {
    return false;
  }
  
  // Check if all digits are the same
  if (/^(\d)\1{10}$/.test(cleanedCPF)) {
    return false;
  }
  
  // Validate first check digit
  let sum = 0;
  for (let i = 0; i < 9; i++) {
    sum += parseInt(cleanedCPF[i], 10) * (10 - i);
  }
  let checkDigit = 11 - (sum % 11);
  if (checkDigit >= 10) checkDigit = 0;
  if (checkDigit !== parseInt(cleanedCPF[9], 10)) {
    return false;
  }
  
  // Validate second check digit
  sum = 0;
  for (let i = 0; i < 10; i++) {
    sum += parseInt(cleanedCPF[i], 10) * (11 - i);
  }
  checkDigit = 11 - (sum % 11);
  if (checkDigit >= 10) checkDigit = 0;
  if (checkDigit !== parseInt(cleanedCPF[10], 10)) {
    return false;
  }
  
  return true;
};

/**
 * Validates CNPJ (Brazilian company tax ID)
 * @param cnpj - The CNPJ number
 * @returns True if valid
 */
export const validateCNPJ = (cnpj: string): boolean => {
  const cleanedCNPJ = cnpj.replace(/\D/g, '');
  
  if (cleanedCNPJ.length !== 14) {
    return false;
  }
  
  // Check if all digits are the same
  if (/^(\d)\1{13}$/.test(cleanedCNPJ)) {
    return false;
  }
  
  // Validate first check digit
  let sum = 0;
  let weight = 5;
  for (let i = 0; i < 12; i++) {
    sum += parseInt(cleanedCNPJ[i], 10) * weight;
    weight = weight === 2 ? 9 : weight - 1;
  }
  let checkDigit = sum % 11 < 2 ? 0 : 11 - (sum % 11);
  if (checkDigit !== parseInt(cleanedCNPJ[12], 10)) {
    return false;
  }
  
  // Validate second check digit
  sum = 0;
  weight = 6;
  for (let i = 0; i < 13; i++) {
    sum += parseInt(cleanedCNPJ[i], 10) * weight;
    weight = weight === 2 ? 9 : weight - 1;
  }
  checkDigit = sum % 11 < 2 ? 0 : 11 - (sum % 11);
  if (checkDigit !== parseInt(cleanedCNPJ[13], 10)) {
    return false;
  }
  
  return true;
};

